// Maps the DiscountMatrix DTO to what the Pricing page renders, mirroring the PDF renderer
// (Plan7PdfRenderer) so page and PDF agree. Pure - no React, no store.
import { cornId, soyId } from '../../../logic/store/Seeds/CropsSlice';
import { IRebateDiscountLine, IRebateDiscountMatrix, ISkippedRebate } from '../../../logic/Models/Responses/RebateDiscountMatrixResponse';
import { RoundPrice } from '../../../logic/Utility/CalculationUtilities';

export type PricingCropKey = 'Corn' | 'Soybean';
export const pricingCropKeys: PricingCropKey[] = ['Corn', 'Soybean'];
const cropIdByKey: Record<PricingCropKey, string> = { Corn: cornId, Soybean: soyId };
const cropKeyById: { [cropId: string]: PricingCropKey } = { [cornId]: 'Corn', [soyId]: 'Soybean' };

export type RebateTypeKind = 'LocalGrower' | 'OrderDeadline' | 'EarlyPay' | 'CompetitiveAllowance';

// Same spellings the backend accepts: spaced or compact, trimmed, case-insensitive.
export function parseRebateTypeCode(code: string | null | undefined): RebateTypeKind | undefined
{
	switch ((code ?? '').trim().toUpperCase())
	{
		case 'LOCAL GROWER':
		case 'LOCALGROWER':
			return 'LocalGrower';
		case 'ORDER DEADLINE':
		case 'ORDERDEADLINE':
			return 'OrderDeadline';
		case 'EARLY PAY':
		case 'EARLYPAY':
			return 'EarlyPay';
		case 'COMPETITIVE ALLOWANCE':
		case 'COMPETITIVEALLOWANCE':
			return 'CompetitiveAllowance';
		default:
			return undefined;
	}
}

export type CropAmounts = Partial<Record<PricingCropKey, number>>;

export interface IDiscountSectionSummary
{
	/** RebateTypeName off the first line, or the legacy PDF literal. */
	title: string;
	benefitName?: string;
	discountTotal: number;
	discountByCrop: CropAmounts;
	/** Running net after this section in display order (list − Early Order − Local Grower). */
	netAfter: number;
	/** Per-crop EffectiveValue (percent). */
	percentByCrop: CropAmounts;
	percent: number;
	/** yyyy-MM-dd; Order Deadline only. */
	expiration?: string;
}

export interface IPricingBreakdown
{
	listTotal: number;
	netTotal: number;
	orderDeadline?: IDiscountSectionSummary;
	localGrower?: IDiscountSectionSummary;
	competitiveAllowance: {
		discountTotal: number;
		discountByCrop: CropAmounts;
		netAfter: number;
	};
	skipped: ISkippedRebate[];
	/** False when the PDF would print no breakdown (no Discounts lines and no CA dollars). */
	hasAnyDiscount: boolean;
}

function byCrop(dictionary: { [cropId: string]: number } | undefined): CropAmounts
{
	const result: CropAmounts = {};
	pricingCropKeys.forEach(key =>
	{
		const value = dictionary?.[cropIdByKey[key]];
		if (typeof value === 'number')
		{
			result[key] = value;
		}
	});
	return result;
}

function summarizeLines(lines: IRebateDiscountLine[], fallbackTitle: string): IDiscountSectionSummary | undefined
{
	if (!lines.length)
	{
		return undefined;
	}

	const discountByCrop: CropAmounts = {};
	const percentByCrop: CropAmounts = {};
	pricingCropKeys.forEach(key =>
	{
		const contributing = lines.filter(l => l.ByCrop?.[cropIdByKey[key]]);
		if (contributing.length)
		{
			discountByCrop[key] = contributing.reduce((acc, l) => acc + l.ByCrop[cropIdByKey[key]].Discount, 0);
			percentByCrop[key] = contributing[0].ByCrop[cropIdByKey[key]].EffectiveValue;
		}
	});

	return {
		title: lines[0].RebateTypeName || fallbackTitle,
		benefitName: lines[0].BenefitName || undefined,
		discountTotal: lines.reduce((acc, l) => acc + l.TotalDiscount, 0),
		discountByCrop,
		netAfter: 0, // set by buildPricingBreakdown
		percentByCrop,
		percent: lines[0].Value,
	};
}

export function buildPricingBreakdown(matrix: IRebateDiscountMatrix): IPricingBreakdown
{
	const discounts = matrix.Discounts ?? [];
	const linesOf = (kind: RebateTypeKind) => discounts.filter(l => parseRebateTypeCode(l.RebateTypeCode) === kind);

	const orderDeadline = summarizeLines(linesOf('OrderDeadline'), 'Early Order');
	if (orderDeadline && matrix.EarlyOrderExpiration)
	{
		orderDeadline.expiration = matrix.EarlyOrderExpiration;
	}
	const localGrower = summarizeLines(linesOf('LocalGrower'), 'Grower Discount');

	// Running nets follow display order (the calculator subtracts CA first); the CA row is matrix.NetTotal.
	let running = matrix.ListTotal;
	[orderDeadline, localGrower].forEach(section =>
	{
		if (section)
		{
			running = RoundPrice(running - section.discountTotal);
			section.netAfter = running;
		}
	});

	return {
		listTotal: matrix.ListTotal,
		netTotal: matrix.NetTotal,
		orderDeadline,
		localGrower,
		competitiveAllowance: {
			discountTotal: matrix.CompetitiveAllowanceTotal,
			discountByCrop: byCrop(matrix.CompetitiveAllowanceByCrop),
			netAfter: matrix.NetTotal,
		},
		skipped: matrix.Skipped ?? [],
		hasAnyDiscount: discounts.length > 0 || matrix.CompetitiveAllowanceTotal > 0,
	};
}

export function findSkipped(matrix: IRebateDiscountMatrix | undefined, kind: RebateTypeKind): ISkippedRebate | undefined
{
	return matrix?.Skipped?.find(s => parseRebateTypeCode(s.RebateTypeCode) === kind);
}

export interface IEarlyPayCell
{
	netTotal: number;
	netPerBagByCrop: CropAmounts;
}

export interface IEarlyPayRow
{
	/** yyyy-MM-dd; DisplayDate ?? EffectiveEndDate. */
	date: string;
	/** The earliest window is the one live at the pricing date. */
	isCurrent: boolean;
	byPaymentType: { [paymentType: string]: IEarlyPayCell };
}

export interface IEarlyPayTable
{
	/** Distinct PaymentType values in first-seen order - one column group each. */
	paymentTypes: string[];
	rows: IEarlyPayRow[];
}

const windowDate = (line: IRebateDiscountLine) => line.DisplayDate ?? line.EffectiveEndDate ?? undefined;
const paymentTypeOf = (line: IRebateDiscountLine) => (line.PaymentType ?? '').trim();

// One row per window date (ascending), one column group per payment type. Lines with no date or
// payment type are dropped, as the PDF drops them; first line wins per (date, type).
export function buildEarlyPayTable(matrix: IRebateDiscountMatrix): IEarlyPayTable
{
	const lines = (matrix.PaymentOptions ?? []).filter(l => windowDate(l) && paymentTypeOf(l));
	const paymentTypes = Array.from(new Set(lines.map(paymentTypeOf)));
	const dates = Array.from(new Set(lines.map(windowDate))).sort();

	const rows = dates.map((date, index) =>
	{
		const byPaymentType: { [paymentType: string]: IEarlyPayCell } = {};
		paymentTypes.forEach(type =>
		{
			const line = lines.find(l => windowDate(l) === date && paymentTypeOf(l) === type);
			if (line)
			{
				const netPerBagByCrop: CropAmounts = {};
				pricingCropKeys.forEach(key =>
				{
					const values = line.ByCrop?.[cropIdByKey[key]];
					if (values)
					{
						netPerBagByCrop[key] = values.NetPerBag;
					}
				});
				byPaymentType[type] = { netTotal: line.NetTotal, netPerBagByCrop };
			}
		});
		return { date, isCurrent: index === 0, byPaymentType };
	});

	return { paymentTypes, rows };
}

const usd = Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export function formatCurrency(value: number | undefined): string
{
	return typeof value === 'number' ? usd.format(value) : '';
}

/** 'yyyy-MM-dd' → 'MM/DD/YY' without going through Date (no timezone shift). */
export function formatShortDate(isoDate: string | null | undefined): string
{
	const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate ?? '');
	return match ? `${match[2]}/${match[3]}/${match[1].slice(2)}` : '';
}

// Re-prices the saved matrix with the draft's CA dollars and Local Grower percent so figures track
// edits before Save. Mirrors RebateDiscountCalculator: per crop, discount groups run in
// OrderOfOperation order against the running net; group 0 runs against list total and then
// subtracts CA; Early Pay runs against the final net. Per-bag/acre figures scale by the saved ratio.

export interface IDraftPricingInputs
{
	competitiveAllowanceByCrop: CropAmounts;
	/** Effective Local Grower percent; undefined leaves the saved value in place. */
	localGrowerPercent?: number;
}

function rawDiscount(measureType: string | null | undefined, value: number, base: number): number
{
	if ((measureType ?? '').toUpperCase() === 'FIXEDAMOUNT')
	{
		return RoundPrice(Math.min(value, base));
	}
	return RoundPrice(base * value / 100);
}

function scaled(previousNet: number, previousPer: number, net: number): number
{
	const units = previousPer > 0 ? previousNet / previousPer : 0;
	return units > 0 ? RoundPrice(net / units) : 0;
}

export function estimateMatrix(matrix: IRebateDiscountMatrix, draft: IDraftPricingInputs): IRebateDiscountMatrix
{
	const discounts = (matrix.Discounts ?? []).map(l => ({ ...l, ByCrop: { ...l.ByCrop } }));
	const netByCrop: { [cropId: string]: number } = {};
	const competitiveAllowanceByCrop: { [cropId: string]: number } = {};

	Object.keys(matrix.ListTotalByCrop ?? {}).forEach(cropId =>
	{
		const ca = draft.competitiveAllowanceByCrop[cropKeyById[cropId]] ?? 0;
		competitiveAllowanceByCrop[cropId] = ca;

		const linesForCrop = discounts.filter(l => l.ByCrop[cropId]);
		const groupKeys = Array.from(new Set([...linesForCrop.map(l => l.OrderOfOperation), 0])).sort((a, b) => a - b);

		let currentBase = matrix.ListTotalByCrop[cropId];
		groupKeys.forEach(key =>
		{
			const groupBase = currentBase;
			let groupDiscountSum = 0;
			linesForCrop.filter(l => l.OrderOfOperation === key).forEach(line =>
			{
				const previous = line.ByCrop[cropId];
				const value = parseRebateTypeCode(line.RebateTypeCode) === 'LocalGrower' && typeof draft.localGrowerPercent === 'number'
					? draft.localGrowerPercent
					: previous.EffectiveValue;
				const raw = rawDiscount(line.MeasureType, value, groupBase);
				line.ByCrop[cropId] = { ...previous, Base: groupBase, Discount: raw, EffectiveValue: value, Net: RoundPrice(groupBase - raw) };
				groupDiscountSum += raw;
			});
			let net = groupBase - groupDiscountSum;
			if (key === 0)
			{
				net -= ca;
			}
			currentBase = RoundPrice(net);
		});
		netByCrop[cropId] = currentBase;
	});

	discounts.forEach(line =>
	{
		const values = Object.values(line.ByCrop);
		line.TotalDiscount = RoundPrice(values.reduce((acc, v) => acc + v.Discount, 0));
		line.NetTotal = RoundPrice(values.reduce((acc, v) => acc + v.Net, 0));
	});

	const paymentOptions = (matrix.PaymentOptions ?? []).map(line =>
	{
		const byCropValues: IRebateDiscountLine['ByCrop'] = {};
		Object.keys(line.ByCrop ?? {}).forEach(cropId =>
		{
			const previous = line.ByCrop[cropId];
			const baseNet = netByCrop[cropId] ?? previous.Base;
			const raw = rawDiscount(line.MeasureType, previous.EffectiveValue, baseNet);
			const net = RoundPrice(RoundPrice(baseNet - raw) + previous.CustomTreatmentTotal);
			byCropValues[cropId] = {
				...previous,
				Base: baseNet,
				Discount: raw,
				Net: net,
				NetPerBag: scaled(previous.Net, previous.NetPerBag, net),
				NetPerAcre: scaled(previous.Net, previous.NetPerAcre, net),
			};
		});
		const values = Object.values(byCropValues);
		return {
			...line,
			ByCrop: byCropValues,
			TotalDiscount: RoundPrice(values.reduce((acc, v) => acc + v.Discount, 0)),
			NetTotal: RoundPrice(values.reduce((acc, v) => acc + v.Net, 0)),
		};
	});

	const sum = (dictionary: { [cropId: string]: number }) => RoundPrice(Object.values(dictionary).reduce((acc, v) => acc + v, 0));

	return {
		...matrix,
		Discounts: discounts,
		CompetitiveAllowanceByCrop: competitiveAllowanceByCrop,
		CompetitiveAllowanceTotal: sum(competitiveAllowanceByCrop),
		NetByCrop: netByCrop,
		NetTotal: sum(netByCrop),
		PaymentOptions: paymentOptions,
	};
}
